import { config } from '@/forms/config';
import { ValidationAdapter } from '@/forms/core';
import type { FormConditionalValidation, FormValidationSchema } from '@/forms/typings';
import { generateConditionalValidation } from './generateConditionalValidation';
import { generateConditionalValidationFieldIds } from './generateConditionalValidationFieldIds';

const hasPrimitiveValue = (value: unknown): boolean => {
  if (value === undefined || value === null || value === '' || value === false) return false;
  if (Array.isArray(value)) return value.length > 0;
  return true;
};

const COMPLETE_ENTRY_MESSAGE = 'At least one entry must be fully completed.';

// Providers with nothing to list pass by ticking the box, so the message names it.
const COMPLETE_ENTRY_OR_NOT_APPLICABLE_MESSAGE =
  'Select Not Applicable or fully complete at least one entry.';

/**
 * Yup schema for the `{baseFieldId}_atLeastOneComplete` synthetic key.
 * Passes when at least one discovered array instance has all `requiredChildSuffixes`
 * filled in the Formik root values, or is marked Not Applicable. Gated by `conditions`
 * (e.g. should-validate).
 */
export const createAtLeastOneCompleteInstanceYupSchema = (
  baseFieldId: string,
  requiredChildSuffixes: string[],
  conditions: FormConditionalValidation[] | undefined,
  offersNotApplicable = false,
): FormValidationSchema => {
  const Yup = ValidationAdapter.getLibrary();
  const base = Yup.mixed().nullable();

  const tested = base.test({
    name: 'atLeastOneCompleteInstance',
    message: offersNotApplicable
      ? COMPLETE_ENTRY_OR_NOT_APPLICABLE_MESSAGE
      : COMPLETE_ENTRY_MESSAGE,
    test() {
      const root = this.parent as Record<string, unknown> | undefined;
      if (!root) return true;
      if (!requiredChildSuffixes.length) return true;

      // Discover all instance indices from keys like {baseFieldId}_$N_{anything}
      const instancePrefix = `${baseFieldId}_$`;
      const indices = new Set<string>();
      for (const key of Object.keys(root)) {
        if (!key.startsWith(instancePrefix)) continue;
        const rest = key.slice(instancePrefix.length);
        const sep = rest.indexOf('_');
        if (sep === -1) continue;
        const n = rest.slice(0, sep);
        if (/^\d+$/.test(n)) indices.add(n);
      }

      if (indices.size === 0) return false;

      // Ticking Not Applicable declares there are no entries to complete.
      if (
        [...indices].some(
          n =>
            root[`${baseFieldId}_$${n}${config.notApplicableFieldSuffix}`] ===
            true,
        )
      )
        return true;

      return [...indices].some(n =>
        requiredChildSuffixes.every(suffix =>
          hasPrimitiveValue(root[`${baseFieldId}_$${n}_${suffix}`]),
        ),
      );
    },
  });

  if (!conditions?.length) return tested as unknown as FormValidationSchema;

  return base.when(
    generateConditionalValidationFieldIds(conditions),
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (values: any[], schema: any) =>
      generateConditionalValidation(conditions, values) ? tested : schema,
  ) as unknown as FormValidationSchema;
};
